import React, { useEffect, useMemo, useState } from "react";
import styles from "./activeBlogCategories.module.scss";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CustomButton from "../../components/customComponents/customButton/CustomButton";
import { GenCustomTable } from "../../components/customComponents/genCustomTable/GenCustomTable";
import { connect, useDispatch } from "react-redux";
import {
  getActiveBlogCategory,
  getAllBlogCategory,
  createBlogCategory,
  updateBlogCategory,
} from "../../store/actions";

const columns = [
  { id: 1, label: "S.NO", minWidth: 120, align: "center" },
  { id: 2, label: "Category", minWidth: 170, align: "center" },
  { id: 3, label: "Description", minWidth: 170, align: "center" },
  { id: 4, label: "Blogs", minWidth: 120, align: "center" },
  { id: 5, label: "Action", minWidth: 170, align: "center" },
];

const isCategoryActive = (category = {}) => {
  if (category?.status !== undefined) {
    return [1, "1", "ACTIVE", true].includes(category.status);
  }
  if (category?.active !== undefined) {
    return Boolean(category.active);
  }
  return false;
};

const defaultForm = {
  name: "",
  description: "",
  status: 1,
  active: true,
};

const ActiveBlogCategories = ({ categories = [] }) => {
  const dispatch = useDispatch();
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupItems, setPopupItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    dispatch(getActiveBlogCategory());
  }, [dispatch]);

  const activeCategories = useMemo(
    () => categories.filter((category) => isCategoryActive(category)),
    [categories]
  );

  const openBlogsPopup = (category = {}) => {
    const blogs = category?.blogs || category?.blogList;
    const normalized = Array.isArray(blogs)
      ? blogs
          .map((item) =>
            typeof item === "string" ? item : item?.title || item?.name
          )
          .filter(Boolean)
      : [];
    setPopupItems(normalized);
    setPopupOpen(true);
  };

  const closePopup = () => setPopupOpen(false);

  const handleEdit = (category = {}) => {
    if (!category) return;
    setEditingCategory(category);
    const statusValue = isCategoryActive(category) ? 1 : 0;
    setFormData({
      name: category?.name || "",
      description: category?.description || "",
      status: statusValue === 0 ? 0 : 1,
      active: statusValue === 1,
    });
    setErrors({});
    setFormOpen(true);
  };

  const handleAdd = () => {
    setFormData({ ...defaultForm });
    setErrors({});
    setEditingCategory(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (isSubmitting) return;
    setFormOpen(false);
    setFormData({ ...defaultForm });
    setEditingCategory(null);
    setErrors({});
  };

  const handleFieldChange = (field) => (event) => {
    const value =
      field === "status" ? (event.target.checked ? 1 : 0) : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "status" ? { active: value === 1 } : {}),
    }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = "Category name is required";
    if (!formData.description.trim())
      nextErrors.description = "Description is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const refreshLists = () => {
    dispatch(getActiveBlogCategory());
    dispatch(getAllBlogCategory());
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      status: formData.status,
      active: formData.status === 1,
    };

    setIsSubmitting(true);

    const callbacks = {
      success: () => {
        setIsSubmitting(false);
        closeForm();
        refreshLists();
      },
      failure: () => {
        setIsSubmitting(false);
      },
    };

    if (editingCategory?.id) {
      dispatch(
        updateBlogCategory({
          payload: { ...payload, id: editingCategory.id },
          callback: callbacks,
        })
      );
    } else {
      dispatch(
        createBlogCategory({
          payload,
          callback: callbacks,
        })
      );
    }
  };

  const getStructuredRows = (data = []) =>
    data.map((item, idx) => ({
      id: item?.id ?? idx,
      rColumns: [
        idx + 1,
        item?.name || item?.title || "-",
        <div className={styles.desContainer}>{item?.description || item?.miniDescription || "-"}</div>,
        <Button
          key={`btn-${item?.id ?? idx}`}
          variant="outlined"
          className={styles.ovalButtonOut}
          disableRipple
          disableElevation
          onClick={() => openBlogsPopup(item)}
        >
          View
        </Button>,
        <div className={styles.imgtable} onClick={() => handleEdit(item)}>
          <EditIcon color="primary" />
          EDIT
        </div>,
      ],
    }));

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.topWrapper}>
          <div className={styles.top}>Active Blog Categories</div>
          <div className={styles.search}>
            <CustomButton
              title="Add"
              txtClr="#FFFFFF"
              background="black"
              width="141px"
              height="48px"
              borderRadius="12px"
              onClick={handleAdd}
            />
          </div>
        </div>
        <div className={styles.bottom}>
          <GenCustomTable
            columns={columns}
            rows={getStructuredRows(activeCategories)}
          />
        </div>
      </div>
      <Dialog
        open={popupOpen}
        onClose={closePopup}
        maxWidth="sm"
        fullWidth
        hideBackdrop
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "16px",
            backgroundColor: "#fff",
            zIndex: 1301,
          },
        }}
        sx={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <DialogTitle>
          Blogs
          <IconButton
            onClick={closePopup}
            sx={{ position: "absolute", right: 8, top: 8, color: "#000" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {popupItems.length > 0 ? (
            <ul>
              {popupItems.map((name, idx) => (
                <li key={idx}>{name}</li>
              ))}
            </ul>
          ) : (
            <Box>No blogs linked with this category</Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={formOpen}
        onClose={closeForm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit,
          sx: { borderRadius: "12px", padding: "8px" },
        }}
      >
        <DialogTitle>
          {editingCategory ? "Edit Blog Category" : "Add Blog Category"}
          <IconButton
            onClick={closeForm}
            sx={{ position: "absolute", right: 8, top: 8, color: "#000" }}
            disabled={isSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} mt={1}>
            <FormControl fullWidth error={Boolean(errors.name)}>
              <TextField
                label="Category Name"
                value={formData.name}
                onChange={handleFieldChange("name")}
                placeholder="Enter blog category name"
                disabled={isSubmitting}
              />
              {errors.name ? (
                <FormHelperText>{errors.name}</FormHelperText>
              ) : null}
            </FormControl>
            <FormControl fullWidth error={Boolean(errors.description)}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={handleFieldChange("description")}
                placeholder="Enter description"
                multiline
                minRows={3}
                disabled={isSubmitting}
              />
              {errors.description ? (
                <FormHelperText>{errors.description}</FormHelperText>
              ) : null}
            </FormControl>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Status
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography variant="body2">Inactive</Typography>
                <Switch
                  checked={formData.status === 1}
                  onChange={handleFieldChange("status")}
                  disabled={isSubmitting}
                  inputProps={{ "aria-label": "category status" }}
                  sx={{
                    transform: "scale(1.35)",
                    ".MuiSwitch-thumb": { boxShadow: "none" },
                    ".MuiSwitch-switchBase": {
                      padding: 1,
                    },
                  }}
                />
                <Typography variant="body2">Active</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeForm} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            {editingCategory ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  categories: state?.blogcategory?.categories,
});

export default connect(mapStateToProps, null)(ActiveBlogCategories);
